/*global Daon */
/*eslint no-undef: "error"*/

import React, { Component } from "react";
import { func, number, instanceOf, bool } from "prop-types";
import { connect } from "react-redux";
import { clearMessages } from "../../logic/actions/faceLiveness3D";

import FaceCaptureUI from "../FaceCaptureUI";
import { getLabelForResult, getLabelForMessage } from "./labels";
import { isMobile } from "../../logic/deviceType";
import frameOverlayGreen from "../../assets/frame_overlay_far_green.png";
import frameOverlayRed from "../../assets/frame_overlay_far_red.png";
import {
  faceLivenessMovementDuration,
  faceLivenessMovementDelay,
} from "../../logic/faceLivenessTimingConfiguration";
import "./style.css";

const UPDATE_TYPES = Daon.FaceLiveness3D.UPDATE_TYPES;
const animationStepDuration = 40; // 25fps

export class FaceLiveness3DUI extends Component {
  static propTypes = {
    width: number,
    height: number,
    onInitialized: func,
    onCameraStarted: func,
    instance: instanceOf(Object),
    retryCameraInit: bool,
  };

  state = {
    animationStart: false,
    isAnimationCalled: false,
    finished: false,
    reinitCamera: false,
    counter: faceLivenessMovementDelay / 1000,
    isCounterActive: false,
  };
  constructor(props) {
    super(props);
    this.counterTimer = this.counterTimer.bind(this);
  }

  componentDidMount() {
    this.canvas = this.refs.canvas;
    this.ctx = this.canvas.getContext("2d");
    this.text = this.refs.text;

    this.imageGreen = new Image();
    this.imageGreen.src = frameOverlayGreen;

    this.imageRed = new Image();
    this.imageRed.src = frameOverlayRed;

    this.imageGreen.onload = () => {
      this.props.greenOverlayLoaded();
    };

    this.imageRed.onload = () => {
      this.props.redOverlayLoaded();
      this.drawOutline(this.imageRed);
    };

    this.props.clearMessages();
  }

  shouldComponentUpdate(prevProps) {
    return (
      prevProps.isReady !== this.props.isReady ||
      prevProps.message !== this.props.message ||
      prevProps.result !== this.props.result ||
      prevProps.retryCameraInit !== this.props.retryCameraInit ||
      prevProps.isRunning !== this.props.isRunning ||
      prevProps.finished !== this.props.finished ||
      prevProps.result !== this.props.result
    );
  }

  static getDerivedStateFromProps(props, state) {
    if (props.message !== state.message) {
      return {
        message: props.message,
      };
    }

    if (props.isReady !== state.isReady) {
      return {
        isReady: props.isReady,
      };
    }

    if (props.result === "PASS" || props.result === "FAIL")
      return { result: props.result };

    if (props.isRunning && props.isRunning !== state.isRunning)
      return { isRunning: props.isRunning };

    if (props.finished && props.finished !== state.finished)
      return { finished: props.finished };

    if (props.retryCameraInit !== state.retryCameraInit)
      return { retryCameraInit: props.retryCameraInit };

    return null;
  }

  componentDidUpdate() {
    switch (this.state.message) {
      case UPDATE_TYPES.READY:
        break;
      case UPDATE_TYPES.AWAIT_RESULTS:
        this.setState({ finished: true });
        break;
      case UPDATE_TYPES.HOLD:
        this.setState({ isCounterActive: true });
        this.drawOutline(this.imageGreen);
        this.counterInterval = setInterval(this.counterTimer, 1000);
        break;
      case UPDATE_TYPES.MOVE_CLOSER:
        this.startAnimation(faceLivenessMovementDuration);
        break;
      default:
        break;
    }

    if (this.state.retryCameraInit) this.reinitCamera();
  }

  counterTimer() {
    if (this.state.counter === 1) {
      this.setState({ isCounterActive: false });
      clearInterval(this.counterInterval);
    } else {
      this.setState({ counter: this.state.counter - 1 });
      this.forceUpdate();
    }
  }

  drawOutline(img) {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    let scale = 0.55;
    if (this.canvas.width < this.canvas.height) {
      scale = isMobile(navigator.userAgent) ? 0.75 : 0.75;
    }

    let dx = (this.canvas.width - img.width * scale) / 2;
    let dy = (this.canvas.height - img.height * scale * 1.4) / 2;

    this.ctx.globalAlpha = 0.7;
    this.ctx.drawImage(
      img,
      dx,
      dy,
      img.width * scale,
      img.height * scale * 1.4
    );
  }

  startAnimation(duration) {
    if (!this.state.isAnimationCalled)
      this.setState({ isAnimationCalled: true });
    const animationStartTS = performance.now();
    const animationStopTS = animationStartTS + duration;
    const animationScalePerSec = ((1.6 - 1) / duration) * 1000;
    this.stepAnimation(animationStartTS, animationStopTS, animationScalePerSec);
  }

  stepAnimation = (animationStartTS, animationStopTS, animationScalePerSec) => {
    const currentTS = performance.now();
    if (currentTS < animationStopTS) {
      let newScale =
        1 + (animationScalePerSec * (currentTS - animationStartTS)) / 1000;

      this.ctx.setTransform(
        newScale,
        0,
        0,
        newScale,
        (-this.canvas.width * (newScale - 1)) / 2,
        (-this.canvas.height * (newScale - 1)) / 2
      );
      this.drawOutline(this.imageGreen);
      setTimeout(
        () =>
          this.stepAnimation(
            animationStartTS,
            animationStopTS,
            animationScalePerSec
          ),
        animationStepDuration
      );
    } else {
      console.log("skip stepAnimation");
    }
  };

  reinitCamera() {
    this.setState({ reinitCamera: true });
  }

  render() {
    let instructionsStyle = this.state.isCounterActive
      ? "instructions-on-face-countdown cyan"
      : "instructions-on-face cyan";
    let instuctions;
    if (this.props.message === UPDATE_TYPES.HOLD) {
      instructionsStyle = "instructions-on-face-countdown cyan";
      instuctions = this.state.counter;
    } else {
      instuctions =
        getLabelForResult(this.state.result, this.props.score) ||
        getLabelForMessage(this.props.message, UPDATE_TYPES);
    }

    return (
      <>
        <FaceCaptureUI
          onCameraStarted={this.props.onCameraStarted}
          width={this.props.width}
          cameraPermissionDenied={this.props.cameraPermissionDenied}
          retryCameraInit={this.props.retryCameraInit}
          isReady={this.props.isReady}
        />
        <p className={instructionsStyle}>{instuctions}</p>
        {!this.state.finished ? (
          <canvas
            className="ui-canvas-container ui-canvas"
            ref="canvas"
            width={480}
            height={640}
          />
        ) : null}
      </>
    );
  }
}

export default connect(
  (state) => {
    return { ...state.faceLiveness3D };
  },
  {
    clearMessages,
  }
)(FaceLiveness3DUI);
